/**
 * The grid a form's fields share, so right edges line up on a wide screen and
 * the columns collapse in order on a narrow one.
 *
 * A field takes more than one column with a literal container-query span on
 * its `FormField`, such as `@md:col-span-2 @4xl:col-span-4`. Spans in a row add
 * to the column count; a row that does not fill leaves a ragged edge.
 *
 * @module components/ui/Form/FormGrid
 */

import React, { type ReactNode } from "react";

export const FORM_GRID_COLUMNS = [2, 3, 4] as const;
export type FormGridColumns = (typeof FORM_GRID_COLUMNS)[number];

/**
 * Columns follow the width of the grid's own container, not the viewport: the
 * same form sits in a full-width panel and in a pane beside a list.
 */
const COLUMN_CLASS: Record<FormGridColumns, string> = {
  2: "@xl:grid-cols-2",
  3: "@md:grid-cols-2 @3xl:grid-cols-3",
  4: "@md:grid-cols-2 @4xl:grid-cols-4",
};

export interface FormGridProps {
  /** Columns at `lg`. */
  columns?: FormGridColumns;
  children: ReactNode;
  className?: string;
  "data-testid"?: string;
}

export function FormGrid({
  columns = 4,
  children,
  className = "",
  "data-testid": testId,
}: FormGridProps): React.ReactElement {
  return (
    // `className` lands on the container, the element the surrounding layout
    // sees: size containment gives it no intrinsic width, so a flex row needs
    // `flex-1` here, not on the grid inside.
    <div className={`@container ${className}`.trim()} data-testid={testId}>
      <div className={`grid grid-cols-1 gap-x-4 gap-y-3 ${COLUMN_CLASS[columns]}`}>{children}</div>
    </div>
  );
}
